import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { DripJourney } from '../api/types'
import { Icon } from '../components/Icon'
import { readable } from '../components/Playbooks'
import { KIND, KindIcon, StepTrail } from '../components/StepKind'
import { ErrorState, Loading } from '../components/States'
import { useConfirm } from '../components/Confirm'
import { useToast } from '../components/Toast'
import { relativeDays, shortDateTime, titleCase } from '../lib/format'
import { useAsync } from '../lib/useAsync'
import { DRIP_STATUS } from './DripCampaigns'

/** A drip's report: is it reaching people, are they answering, and who is where.
 *  A draft shows its setup and a Launch button instead of empty numbers. */
export function DripDetail() {
  const { dripId = '' } = useParams()
  const [bump, setBump] = useState(0)
  const { data: d, error, loading } = useAsync(() => api.drip(Number(dripId)), [dripId, bump])
  const { data: journey } = useAsync(() => api.dripJourney(Number(dripId)), [dripId, bump])
  const [busy, setBusy] = useState(false)
  // Which journey step the People table is narrowed to: a node index, 'done', or null for everyone.
  const [focus, setFocus] = useState<number | 'done' | null>(null)
  const nav = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()

  async function status(next: 'active' | 'paused' | 'stopped', message: string) {
    if (next === 'stopped' && !await confirm({
      title: 'Stop this drip for everyone?',
      body: 'People already on it won’t get any more steps.',
      confirmLabel: 'Stop the drip', danger: true,
    })) return
    setBusy(true)
    try {
      await api.setDripStatus(Number(dripId), next)
      toast(message, { tone: 'positive' })
      setBump((n) => n + 1)
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  if (loading && !d) return <div className="page"><Loading /></div>
  if (error || !d) return <div className="page"><ErrorState error={error} /></div>

  const s = d.stats
  const nodes = journey?.nodes ?? []
  // Where someone is waiting: the first action step at or after their place in the workflow.
  const waitingAt = (step: number, status: string) =>
    status === 'active' || status === 'paused' ? nodes.find((n) => n.index >= step)?.index ?? null : null
  const people = (d.people ?? []).filter((p) =>
    focus === null ? true : focus === 'done' ? p.status === 'done' : waitingAt(p.step, p.status) === focus)
  const focusLabel = focus === null ? null : focus === 'done'
    ? 'finished the whole drip'
    : `waiting at step ${nodes.findIndex((n) => n.index === focus) + 1}`
  const badge = DRIP_STATUS[d.status]
  const tiles: [string, number, string?][] = [
    ['People', s.people], ['Reached so far', s.messages.sent + s.messages.test_mode],
    ['Replied', s.replied, 'var(--positive)'], ['Applied', s.applied, 'var(--accent)'],
    ['Finished', s.finished], ['Waiting to start', s.not_started],
  ]

  return (
    <div className="page campaign-page">
      <div className="crumbs"><Link to="/drips">Drip campaigns</Link> › {d.name}</div>
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <h1 className="h1">{d.name}</h1>
            <span className={`badge ${badge.tone}`}>{badge.label}</span>
          </div>
          <p className="subline">
            Runs <strong>{d.workflow_name}</strong> · {d.daily_limit} new people a day
            {d.stop_on_reply ? ' · stops on reply' : ''}{d.auto_join ? ' · adds new matches' : ''}
            {d.launched_at ? ` · launched ${relativeDays(d.launched_at)}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => nav(`/drips/${d.id}/edit`)}><Icon name="tune" size={16} /> Settings</button>
          {d.status === 'draft' ? (
            <button className="btn primary" onClick={() => nav(`/drips/${d.id}/edit`)}><Icon name="send" size={16} /> Review &amp; launch</button>
          ) : null}
          {d.status === 'active' ? <button className="btn" disabled={busy} onClick={() => status('paused', 'Paused for everyone')}><Icon name="pause" size={16} /> Pause</button> : null}
          {d.status === 'paused' ? <button className="btn primary" disabled={busy} onClick={() => status('active', 'Resumed')}><Icon name="play_arrow" size={16} /> Resume</button> : null}
          {d.status === 'active' || d.status === 'paused' ? (
            <button className="btn" disabled={busy} onClick={() => status('stopped', 'Stopped for everyone')}><Icon name="stop" size={16} /> Stop</button>
          ) : null}
        </div>
      </div>

      {d.status === 'draft' ? (
        <div className="notice accent" style={{ margin: '14px 0' }}>
          <Icon name="info" />
          <span>This is a draft — nothing has been sent. Open Review &amp; launch to check who it reaches and start it.</span>
        </div>
      ) : (
        <>
          <div className="drip-tiles">
            {tiles.map(([label, n, color]) => (
              <div key={label} className="card drip-tile">
                <div className="drip-tile-n" style={{ color }}>{n.toLocaleString()}</div>
                <div className="quiet" style={{ fontSize: 12 }}>{label}</div>
              </div>
            ))}
          </div>
          <div className="quiet" style={{ fontSize: 12.5, margin: '10px 0 16px' }}>
            Messages: {s.messages.sent} delivered
            {s.messages.test_mode ? ` · ${s.messages.test_mode} recorded in test mode` : ''}
            {s.messages.not_sent ? ` · ${s.messages.not_sent} skipped (no consent or no address)` : ''}
            {Object.keys(s.stop_reasons).length ? ` · Stopped: ${Object.entries(s.stop_reasons).map(([k, v]) => `${v} ${k.toLowerCase()}`).join(', ')}` : ''}
          </div>
          {s.emails.opened || s.emails.clicked ? (
            <div className="quiet" style={{ fontSize: 12.5, margin: '-8px 0 16px' }}
              title="Opens are approximate — some mail apps preload images and others block them. Clicks are exact.">
              Emails: {s.emails.opened} opened (approximate) · {s.emails.clicked} had a link clicked
            </div>
          ) : null}
        </>
      )}

      {nodes.length ? (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="card-head">
            <h2 className="h2">Journey</h2>
            <span className="spacer" />
            <span className="quiet" style={{ fontSize: 12 }}>Click a step to see who’s waiting there</span>
          </div>
          <Journey journey={journey!} focus={focus} onFocus={(f) => setFocus(focus === f ? null : f)} />
        </section>
      ) : null}

      <section className="card">
        <div className="card-head">
          <h2 className="h2">People</h2>
          {focusLabel ? (
            <button className="badge t-accent" style={{ border: 0, cursor: 'pointer', marginLeft: 8 }} onClick={() => setFocus(null)}
              title="Show everyone">
              {focusLabel} ✕
            </button>
          ) : null}
          <span className="spacer" />
          <span className="quiet" style={{ fontSize: 12 }}>
            {people.length}{focus === null && people.length >= 300 ? '+' : ''} shown
          </span>
        </div>
        {!people.length ? (
          <div className="card-body quiet" style={{ paddingTop: 0 }}>
            {focus !== null ? 'Nobody here right now.' : d.status === 'draft' ? 'People are added when you launch.' : 'Nobody yet.'}
          </div>
        ) : (
          <div className="drip-table">
            <div className="drip-tr drip-th"><span>Name</span><span>Progress</span><span>Next step</span><span>Status</span></div>
            {people.map((p) => (
              <div key={p.enrollment_id} className="drip-tr">
                <span>{p.contact_id ? <Link to={`/contacts/c/${p.contact_id}`}>{p.name ?? '—'}</Link> : p.name ?? '—'}</span>
                <span>{nodes.length
                  ? <StepTrail kinds={nodes.map((n) => n.type)} done={nodes.filter((n) => n.index < p.step).length} status={p.status} />
                  : <span className="quiet">{Math.min(p.step, p.steps)} of {p.steps} steps</span>}</span>
                <span className="quiet">{p.status === 'active' ? shortDateTime(p.next_run_at) : '—'}</span>
                <span>
                  {p.stop_reason ? <span className={`badge ${p.stop_reason === 'Replied' || p.stop_reason === 'Applied' || p.stop_reason === 'Funded' ? 't-positive' : 't-neutral'}`}>{p.stop_reason}</span>
                    : <span className={`badge ${p.status === 'active' ? 't-accent' : 't-neutral'}`}>{p.status === 'active' ? (p.step === 0 ? 'Waiting to start' : 'In progress') : titleCase(p.status === 'done' ? 'finished' : p.status)}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

// Timeline spacing: a longer wait draws a longer stretch of track, so the gaps
// read as time — capped so a 90-day wait doesn't push everything off screen.
const COL_W = 200
const gapWidth = (days: number) => Math.max(48, Math.min(280, 36 + days * 12))
const gapText = (d: number) =>
  d % 30 === 0 ? `${d / 30} month${d === 30 ? '' : 's'}` : d % 7 === 0 ? `${d / 7} week${d === 7 ? '' : 's'}` : `${d} day${d === 1 ? '' : 's'}`

/** The drip as a timeline: a track across the top marked by day, the stretch
 *  between stops as long as the wait, and each day's steps hanging below it with
 *  how far they've reached, who's waiting, and what happened. */
function Journey({ journey, focus, onFocus }: {
  journey: DripJourney
  focus: number | 'done' | null
  onFocus: (f: number | 'done') => void
}) {
  const everyone = Math.max(journey.people, 1)
  // Steps that happen on the same day share one stop on the track.
  const days: { day: number; nodes: DripJourney['nodes'] }[] = []
  journey.nodes.forEach((n) => {
    const last = days[days.length - 1]
    if (last && last.day === n.day) last.nodes.push(n)
    else days.push({ day: n.day, nodes: [n] })
  })
  let stepNo = 0

  return (
    <div className="tl">
      {days.map((d, di) => (
        <div key={d.day} style={{ display: 'contents' }}>
          {di > 0 ? (
            <div className="tl-gap" style={{ width: gapWidth(d.day - days[di - 1]!.day) }}>
              <span className="tl-gap-label"><Icon name="schedule" size={12} /> {gapText(d.day - days[di - 1]!.day)}</span>
            </div>
          ) : null}
          <div className={`tl-col${di === 0 ? ' tl-first' : ''}`} style={{ width: COL_W }}>
            <div className="tl-stop">
              <span className="tl-dot" />
              <span className="tl-day">
                {d.day === 0 ? 'Day 0 · start' : `Day ${d.day}`}
                {d.nodes.length > 1 ? <span className="tl-day-n"> · {d.nodes.length} steps, in order</span> : null}
              </span>
            </div>
            {d.nodes.map((n, ni) => {
              stepNo += 1
              const o = n.outcomes
              const isMessage = n.type !== 'task'
              const text = readable(n.type === 'email' ? n.subject ?? '' : n.type === 'task' ? n.title ?? '' : n.body ?? '') || '—'
              return (
                <div key={n.index} style={{ display: 'contents' }}>
                {ni > 0 ? <span className="tl-then"><Icon name="arrow_downward" size={12} /> then, same day</span> : null}
                <button className={`tl-card k-${n.type}${focus === n.index ? ' selected' : ''}`} onClick={() => onFocus(n.index)}>
                  <span className="tl-card-head">
                    <KindIcon kind={n.type} size={22} />
                    <span className="kind-label" style={{ fontSize: 12 }}>{KIND[n.type].short}</span>
                    <span className="quiet" style={{ fontSize: 11, marginLeft: 'auto' }}>Step {stepNo}</span>
                  </span>
                  <span className="tl-title" title={text}>{text}</span>
                  <span className="tl-bar" title={`${n.reached} of ${journey.people} people`}>
                    <span style={{ width: `${Math.round((n.reached / everyone) * 100)}%` }} />
                  </span>
                  <span className="tl-counts">
                    <span><b>{n.reached}</b> {isMessage ? 'reached' : 'done'}</span>
                    <span className={n.waiting ? 'tl-waiting' : 'quiet'}><b>{n.waiting}</b> waiting</span>
                  </span>
                  {isMessage && n.reached ? (
                    <span className="tl-outcomes">
                      {o.sent ? <span><i className="dot-sent" />{o.sent} sent</span> : null}
                      {o.test_mode ? <span><i className="dot-test" />{o.test_mode} test mode</span> : null}
                      {o.not_sent ? <span><i className="dot-skip" />{o.not_sent} skipped</span> : null}
                    </span>
                  ) : null}
                  {n.after.replied || n.after.applied || n.after.stopped ? (
                    <span className="tl-chips">
                      {n.after.replied ? <span className="badge t-positive"><Icon name="reply" size={12} /> {n.after.replied} replied</span> : null}
                      {n.after.applied ? <span className="badge t-accent"><Icon name="description" size={12} /> {n.after.applied} applied</span> : null}
                      {n.after.stopped ? <span className="badge t-neutral">{n.after.stopped} stopped</span> : null}
                    </span>
                  ) : null}
                </button>
                </div>
              )
            })}
          </div>
        </div>
      ))}
      <div className="tl-gap" style={{ width: 48 }} />
      <div className="tl-col tl-last" style={{ width: 150 }}>
        <div className="tl-stop"><span className="tl-dot tl-dot-end"><Icon name="flag" size={11} /></span><span className="tl-day">Finish</span></div>
        <button className={`tl-card${focus === 'done' ? ' selected' : ''}`} onClick={() => onFocus('done')}>
          <span style={{ fontSize: 22, fontWeight: 700 }}>{journey.finished}</span>
          <span className="quiet" style={{ fontSize: 11.5 }}>went all the way through</span>
        </button>
      </div>
    </div>
  )
}
